import { ColumnDef, sortingFns } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import { ControllStatus, getMaturityLevelName } from 'src/api/types';
import { GTCheckBox, GTColumnSelectAnchor, GTExpander, GTFrameworkCell, GTTdm } from 'src/components/GenericTable';
import { formatDate, formatNumber } from 'src/utils/misc';
import { filter } from 'lodash';
import { Link, useNavigate } from 'react-router-dom';
import { GTInternalIds } from 'src/components/GenericTable/types';
import { gTCheckHandler } from 'src/components/GenericTable/util';
import { mpEvent } from 'src/utils/mixpanel/useMixPanel';
import { MPEvents } from 'src/utils/mixpanel/types';
import { TableIds } from 'src/api/types/misc';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { OpenInNew } from '@mui/icons-material';
import { TLLink } from '../DashboardPage/comps';
import { TextOverflowTooltip } from 'src/common';
import { ControlsQuery } from 'src/graphql/_generated';
import { useAuth } from 'src/state/auth';
import { StrategyCell, StatusCell, MaturityCell, AnnualCostCell, DateCell, OwnerCell, LinkCell } from './cells';

type Control = ControlsQuery['controls'][number];

type DeleteModalValue = {
  id: string | null;
  hasScenarios?: number;
  isLive?: boolean;
};

type LinkScenarioModalValue = {
  id: string | null;
  name?: string | null;
  strategy?: string | null;
};

type ActionModalValue = { controlId: string; controlName: string } | null;

type UseColumns = (a: { data?: Control[] }) => {
  columns: ColumnDef<Control>[];
  GTColumnSelectAnchorExported: JSX.Element;
  deleteModal: DeleteModalValue;
  setDeleteModal: (a: DeleteModalValue) => void;
  controlEditId: string | undefined;
  setControlEditId: (id: string | undefined) => void;
  linkScenarioModal: LinkScenarioModalValue;
  setLinkScenarioModal: (data: LinkScenarioModalValue) => void;
  actionModal: ActionModalValue;
  setActionModal: (data: ActionModalValue) => void;
  checked: string[];
  resetChecked: () => void;
  commentsControlId: string | null;
  setCommentsControlId: (id: string | null) => void;
};

const defaultColumns = ['strategy', 'name', 'owner', 'framework_controls', 'status', 'updated_at'];

export const useColumns: UseColumns = ({ data = [] }) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(`${TableIds.rmp}-columns`, defaultColumns);
  const [deleteModal, setDeleteModal] = useState<DeleteModalValue>({ id: null });
  const [linkScenarioModal, setLinkScenarioModal] = useState<LinkScenarioModalValue>({ id: null });
  const [controlEditId, setControlEditId] = useState<string | undefined>(undefined);
  const [actionModal, setActionModal] = useState<ActionModalValue>(null);
  const [checked, setChecked] = useState<string[]>([]);
  const [commentsControlId, setCommentsControlId] = useState<string | null>(null);
  const { features } = useAuth();
  const canInlineEdit = features['app.rmp.inline-edits'];
  const navigate = useNavigate();

  const columns = useMemo(() => {
    const internalIds = [GTInternalIds.expander, GTInternalIds.tdm, GTInternalIds.checker];

    const list: ColumnDef<Control>[] = [
      {
        id: GTInternalIds.checker,
        header: ({ table }) => {
          const { isAllChecked, checkAllHandler } = gTCheckHandler({ checked, selector: 'id', table }, setChecked);

          return <GTCheckBox isChecked={isAllChecked} onClick={checkAllHandler} />;
        },
        cell: ({ row }) => {
          const id = row.original.id;
          return (
            <GTCheckBox
              isChecked={checked.includes(id)}
              onClick={() => {
                if (checked.includes(id)) {
                  setChecked(checked.filter((el) => el !== id));
                } else {
                  setChecked([...checked, id]);
                }
              }}
            />
          );
        },
        size: 1,
        maxSize: 35,
        enableSorting: false,
      },
      {
        id: GTInternalIds.expander,
        header: () => null,
        cell: ({ row }) => {
          return row.getCanExpand() ? (
            <GTExpander onClick={row.getToggleExpandedHandler()} isExpanded={row.getIsExpanded()} />
          ) : null;
        },
        size: 1,
        maxSize: 20,
        enableSorting: false,
      },
      {
        id: 'ux_id',
        header: 'Id',
        accessorFn: (row) => row.id || null,
        cell: (info) => info.getValue(),
      },
      {
        id: 'strategy',
        header: 'Strategy',
        accessorFn: (row) => row.strategy || null,
        cell: ({ row }) => {
          if (!canInlineEdit) return row.original.strategy || '-';
          return <StrategyCell controlId={row.original.id} value={row.original.strategy} />;
        },
      },
      {
        id: 'name',
        header: 'Name',
        accessorFn: (row) => row.name,
        cell: (info) => (
          <TextOverflowTooltip>
            <Link
              to={`/risk-management/control/${info.row.original.id}`}
              className="table-link"
              onClick={(e) => {
                e.stopPropagation();
                mpEvent(MPEvents.Link, {
                  source: {
                    value: ['RMP'],
                  },
                  destination: { value: ['Control'], params: { id: info.row.original.id } },
                  tags: ['RMP'],
                });
              }}
            >
              {info.getValue() as string}
            </Link>
          </TextOverflowTooltip>
        ),
        sortingFn: sortingFns.text,
      },
      {
        id: 'owner',
        header: 'Owner',
        accessorFn: (row) => row.owner?.text,
        cell: ({ row }) => {
          if (!canInlineEdit) return row.original.owner?.text || '-';
          return <OwnerCell controlId={row.original.id} value={row.original.owner} />;
        },
        sortingFn: sortingFns.text,
      },
      {
        id: 'framework_controls',
        header: 'Framework',
        accessorFn: (row) =>
          row.frameworkControls.map((el) => `${el.controlFramework?.shortname} (${el.control_identifier})`).join(', '),
        cell: (info) =>
          info.row.original.frameworkControls.length ? (
            <GTFrameworkCell controls={info.row.original.frameworkControls} />
          ) : (
            '-'
          ),
        sortingFn: sortingFns.text,
      },
      {
        id: 'annual_cost',
        header: 'Annual Cost',
        accessorFn: (row) => row.annualCost,
        cell: ({ row }) => {
          if (!canInlineEdit) return formatNumber(row.original.annualCost, { symbol: true, defaultValue: '-' });
          return <AnnualCostCell controlId={row.original.id} value={row.original.annualCost} />;
        },
        sortingFn: sortingFns.alphanumeric,
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (row) => row.status,
        cell: ({ row }) => {
          if (!canInlineEdit) return row.original.status || '-';
          return <StatusCell controlId={row.original.id} value={row.original.status} />;
        },
      },
      {
        id: 'existingMaturity',
        header: 'Existing Maturity',
        accessorFn: (row) => row.existingMaturity,
        cell: ({ row }) => {
          if (!canInlineEdit) return getMaturityLevelName(row.original.existingMaturity) || '-';
          return (
            <MaturityCell controlId={row.original.id} field="existingMaturity" value={row.original.existingMaturity} />
          );
        },
      },
      {
        id: 'targetMaturity',
        header: 'Target Maturity',
        accessorFn: (row) => row.targetMaturity,
        cell: ({ row }) => {
          if (!canInlineEdit) return getMaturityLevelName(row.original.targetMaturity) || '-';
          return (
            <MaturityCell controlId={row.original.id} field="targetMaturity" value={row.original.targetMaturity} />
          );
        },
      },
      {
        id: 'expectedActiveDate',
        header: 'Expected Go Live',
        accessorFn: (row) => row.expectedActiveDate,
        cell: ({ row }) => {
          if (!canInlineEdit) {
            const date = row.original.expectedActiveDate;
            if (!date) return '-';
            return <TLLink date={date}>{formatDate(date)}</TLLink>;
          }
          return (
            <DateCell controlId={row.original.id} field="expectedActiveDate" value={row.original.expectedActiveDate} />
          );
        },
      },
      {
        id: 'actualActiveDate',
        header: 'Actual Go Live',
        accessorFn: (row) => row.actualActiveDate,
        cell: ({ row }) => {
          if (!canInlineEdit) {
            const date = row.original.actualActiveDate;
            if (!date) return '-';
            return <TLLink date={date}>{formatDate(date)}</TLLink>;
          }
          return (
            <DateCell controlId={row.original.id} field="actualActiveDate" value={row.original.actualActiveDate} />
          );
        },
      },
      {
        id: 'reviewDate',
        header: 'Review Date',
        accessorFn: (row) => row.reviewDate,
        cell: ({ row }) => {
          if (!canInlineEdit) {
            const date = row.original.reviewDate;
            if (!date) return '-';
            return <TLLink date={date}>{formatDate(date)}</TLLink>;
          }
          return <DateCell controlId={row.original.id} field="reviewDate" value={row.original.reviewDate} />;
        },
      },
      {
        id: 'updated_at',
        header: 'Last Edited',
        accessorFn: (row) => row.meta?.updatedAt,
        cell: ({ row }) => {
          const date = row.original.meta?.updatedAt;
          if (!date) return '-';
          return (
            <TLLink date={date}>
              {formatDate(date, {
                formatType: 'datetime',
              })}
            </TLLink>
          );
        },
      },
      {
        id: 'source',
        header: 'Link',
        accessorFn: (row) => row.linkUrl,
        cell: ({ row }) => {
          if (canInlineEdit) {
            return (
              <LinkCell controlId={row.original.id} linkText={row.original.linkText} linkUrl={row.original.linkUrl} />
            );
          }

          const url = row.original.linkUrl;
          const linkText = row.original.linkText || url;

          return url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="table-link"
              onClick={(e) => e.stopPropagation()}
            >
              {linkText}{' '}
              <OpenInNew
                css={`
                  font-size: 14px !important;
                  color: inherit;
                `}
              />
            </a>
          ) : (
            <span>{linkText || '-'}</span>
          );
        },
      },
      {
        id: GTInternalIds.tdm,
        header: () => null,
        cell: ({ row }) => {
          const onViewClick = () => {
            mpEvent(MPEvents.ButtonClick, { button: 'RMP - quick action - View', tags: ['RMP'] });
            mpEvent(MPEvents.Link, {
              source: { value: ['RMP'] },
              destination: { value: ['Control'], params: { id: row.original.id } },
              tags: ['RMP'],
            });
            navigate(`/risk-management/control/${row.original.id}`);
          };
          const onEditClick = () => {
            mpEvent(MPEvents.ButtonClick, { button: 'RMP - quick action - Edit', tags: ['RMP'] });
            setControlEditId(row.original.id);
          };
          const onLinkScenarioClick = () => {
            mpEvent(MPEvents.ButtonClick, { button: 'RMP - quick action - Link Scenario', tags: ['RMP'] });
            setLinkScenarioModal({ id: row.original.id, name: row.original.name, strategy: row.original.strategy });
          };
          const onDeleteClick = () => {
            mpEvent(MPEvents.ButtonClick, { button: 'RMP - quick action - Delete', tags: ['RMP'] });
            setDeleteModal({
              id: row.original.id,
              hasScenarios: row.original.riskManagementPlansCount || 0,
              isLive: row.original.status === ControllStatus.Live,
            });
          };
          const onAddActionClick = () => {
            mpEvent(MPEvents.ButtonClick, { button: 'RMP - quick action - Add Action', tags: ['RMP'] });
            setActionModal({ controlId: row.original.id, controlName: row.original.name });
          };

          return (
            <div css="display: flex;grid-gap: 8px">
              <GTTdm
                onViewClick={onViewClick}
                onEditClick={onEditClick}
                onDeleteClick={onDeleteClick}
                onLinkScenarioClick={onLinkScenarioClick}
                onAddActionClick={onAddActionClick}
                onCommentsClick={() => setCommentsControlId(row.original.id)}
              />
            </div>
          );
        },
        size: 1,
      },
    ];

    return filter(list, (el) => [...internalIds, ...selectedColumns].includes(el.id as string));
  }, [data, selectedColumns, checked]);

  const GTColumnSelectAnchorExported = (
    <GTColumnSelectAnchor
      tableId={TableIds.rmp}
      options={[
        { key: 'ux_id', title: 'Id' },
        { key: 'strategy', title: 'Strategy' },
        { key: 'name', title: 'Name' },
        { key: 'owner', title: 'Owner' },
        { key: 'framework_controls', title: 'Framework' },
        { key: 'annual_cost', title: 'Annual Cost' },
        { key: 'status', title: 'Status' },
        { key: 'existingMaturity', title: 'Existing Maturity' },
        { key: 'targetMaturity', title: 'Target Maturity' },
        { key: 'expectedActiveDate', title: 'Expected Go Live' },
        { key: 'actualActiveDate', title: 'Actual Go Live' },
        { key: 'reviewDate', title: 'Review Date' },
        { key: 'updated_at', title: 'Last Edited' },
        { key: 'source', title: 'Link' },
      ]}
      defaultOptions={defaultColumns}
      value={selectedColumns}
      onChange={setSelectedColumns}
    />
  );

  return {
    columns,
    GTColumnSelectAnchorExported,
    deleteModal,
    setDeleteModal,
    controlEditId,
    setControlEditId,
    checked,
    linkScenarioModal,
    setLinkScenarioModal,
    actionModal,
    setActionModal,
    resetChecked: () => setChecked([]),
    commentsControlId,
    setCommentsControlId,
  };
};
